import React, { useState } from 'react';
import { ApiService } from '../../services/api';
import { Project, ProjectCreate } from '../../types';

interface ProjectCreationFormProps {
  onProjectCreated: (project: Project) => void;
}

const ProjectCreationForm: React.FC<ProjectCreationFormProps> = ({ onProjectCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    tenant: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation errors when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Project name validation
    if (!formData.name.trim()) {
      errors.name = 'Project name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Project name must be at least 3 characters';
    } else if (formData.name.trim().length > 50) {
      errors.name = 'Project name cannot exceed 50 characters';
    }
    
    // Tenant name validation
//     if (!formData.tenant.trim()) {
//       errors.tenant = 'Tenant name is required';
//     } else if (formData.tenant.trim().length < 2) {
//       errors.tenant = 'Tenant name must be at least 2 characters';
//     } else if (formData.tenant.trim().length > 30) {
//       errors.tenant = 'Tenant name cannot exceed 30 characters';
//     }
    
    // Description validation (optional field)
    if (formData.description.trim().length > 200) {
      errors.description = 'Description cannot exceed 200 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Create project data with required created_by field
      const projectData: ProjectCreate = {
        name: formData.name,
        tenant: 'tenant'+formData.name,//formData.tenant,
        description: formData.description || undefined, // Convert empty string to undefined
        created_by: 'system' // In a real app, this would be the current user
      };
      
      // Call API to create project
      const newProject = await ApiService.createProject(projectData);
      
      // Notify parent component
      onProjectCreated(newProject);
      
      // Reset form
      setFormData({
        name: '',
        tenant: '',
        description: ''
      });
      
      setLoading(false);
    } catch (error: any) {
      console.error('Project creation error:', error);
      setError('Failed to create project. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Project Name *
          </label>
          <span className="text-xs text-gray-500" title="The unique name for your project">
            3-50 characters
          </span>
        </div>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            validationErrors.name
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder="Enter project name"
          title="Enter a unique name for your project (3-50 characters)"
        />
        {validationErrors.name && (
          <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
        )}
      </div>

      {/*<div>
        <div className="flex items-center justify-between">
          <label htmlFor="tenant" className="block text-sm font-medium text-gray-700">
            Tenant Name *
          </label>
          <span className="text-xs text-gray-500" title="The organization or department this project belongs to">
            2-30 characters
          </span>
        </div>
        <input
          type="text"
          name="tenant"
          id="tenant"
          value={formData.tenant}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            validationErrors.tenant
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder="Enter tenant name"
          title="Enter the organization or department name (2-30 characters)"
        />
        {validationErrors.tenant && (
          <p className="mt-1 text-sm text-red-600">{validationErrors.tenant}</p>
        )}
      </div>*/}

      <div>
        <div className="flex items-center justify-between">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <span className="text-xs text-gray-500">
            Optional (max 200 characters)
          </span>
        </div>
        <textarea
          name="description"
          id="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            validationErrors.description
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder="Enter project description (optional)"
        />
        <div className="mt-1 flex justify-between">
          <span className="text-xs text-gray-500">
            {validationErrors.description && (
              <span className="text-red-500">{validationErrors.description}</span>
            )}
          </span>
          <span className="text-xs text-gray-500">
            {formData.description.length}/200 characters
          </span>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className={`px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
            loading
              ? 'bg-blue-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {loading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              Creating...
            </div>
          ) : (
            'Create Project'
          )}
        </button>
      </div>
    </form>
  );
};

export default ProjectCreationForm;
